import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { User, Lock, Trash2, Database, Coins, Percent, Settings2, Building2, Phone, Mail, MapPin, FileText } from "lucide-react";

const SettingsPage = () => {
  const { user, fetchUser } = useAuth();
  const [profileData, setProfileData] = useState({
    nom: user?.nom || "",
    prenom: user?.prenom || "",
    email: user?.email || ""
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [globalSettings, setGlobalSettings] = useState({
    taux_commission_ventes: 5.0,
    taux_commission_sav: 3.0,
    tva: 20.0
  });
  const [societe, setSociete] = useState({
    raison_sociale: "",
    siret: "",
    adresse: "",
    code_postal: "",
    ville: "",
    telephone: "",
    email: "",
    tva_intracommunautaire: "",
    forme_juridique: ""
  });
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchGlobalSettings();
    } else {
      setLoadingSettings(false);
    }
  }, [user]);

  const fetchGlobalSettings = async () => {
    try {
      const response = await API.get("/settings");
      setGlobalSettings({
        taux_commission_ventes: response.data.taux_commission_ventes || 5.0,
        taux_commission_sav: response.data.taux_commission_sav || 3.0,
        tva: response.data.tva || 20.0
      });
      if (response.data.societe) {
        setSociete(response.data.societe);
      }
    } catch (error) {
      console.error("Error fetching settings");
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.put(`/users/${user.id}`, profileData);
      await fetchUser();
      toast.success("Profil mis à jour");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.put("/settings", globalSettings);
      toast.success("Paramètres de commission mis à jour");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleSocieteUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.put("/settings", { societe });
      toast.success("Informations société enregistrées");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleClearDemoData = async () => {
    if (!confirm("Êtes-vous sûr de vouloir effacer toutes les données de démonstration ?")) return;
    setLoading(true);
    try {
      await API.delete("/demo/clear");
      toast.success("Données de démonstration effacées");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Paramètres</h1>
        <p className="text-muted-foreground">Gérez votre compte et les paramètres de l'application</p>
      </div>

      {/* Company Info (Admin only) */}
      {user?.role === "admin" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Informations société
            </CardTitle>
            <CardDescription>Ces informations apparaîtront sur les devis, factures et documents</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingSettings ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : (
              <form onSubmit={handleSocieteUpdate} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Raison sociale</Label>
                    <Input
                      value={societe.raison_sociale}
                      onChange={(e) => setSociete({ ...societe, raison_sociale: e.target.value })}
                      placeholder="Nom de la société"
                      data-testid="societe-raison-sociale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Forme juridique</Label>
                    <Input
                      value={societe.forme_juridique}
                      onChange={(e) => setSociete({ ...societe, forme_juridique: e.target.value })}
                      placeholder="SASU, SARL, SAS..."
                      data-testid="societe-forme"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5" />
                      SIRET
                    </Label>
                    <Input
                      value={societe.siret}
                      onChange={(e) => setSociete({ ...societe, siret: e.target.value })}
                      placeholder="N° SIRET"
                      data-testid="societe-siret"
                    />
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      Adresse
                    </Label>
                    <Input
                      value={societe.adresse}
                      onChange={(e) => setSociete({ ...societe, adresse: e.target.value })}
                      placeholder="Rue, boulevard..."
                      data-testid="societe-adresse"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Code postal</Label>
                    <Input
                      value={societe.code_postal}
                      onChange={(e) => setSociete({ ...societe, code_postal: e.target.value })}
                      placeholder="97110"
                      data-testid="societe-cp"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input
                      value={societe.ville}
                      onChange={(e) => setSociete({ ...societe, ville: e.target.value })}
                      placeholder="Pointe-à-Pitre"
                      data-testid="societe-ville"
                    />
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5" />
                      Téléphone
                    </Label>
                    <Input
                      value={societe.telephone}
                      onChange={(e) => setSociete({ ...societe, telephone: e.target.value })}
                      placeholder="0690..."
                      data-testid="societe-telephone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" />
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={societe.email}
                      onChange={(e) => setSociete({ ...societe, email: e.target.value })}
                      placeholder="contact@societe.com"
                      data-testid="societe-email"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>N° TVA intracommunautaire</Label>
                    <Input
                      value={societe.tva_intracommunautaire}
                      onChange={(e) => setSociete({ ...societe, tva_intracommunautaire: e.target.value })}
                      placeholder="FR..."
                      data-testid="societe-tva"
                    />
                  </div>
                </div>

                {/* Preview */}
                <div className="p-4 rounded-lg bg-secondary/50 space-y-1">
                  <p className="text-sm font-semibold">{societe.raison_sociale || "Nom de la société"}</p>
                  <p className="text-xs text-muted-foreground">{societe.forme_juridique} - SIRET: {societe.siret}</p>
                  <p className="text-xs text-muted-foreground">{societe.adresse}, {societe.code_postal} {societe.ville}</p>
                  <p className="text-xs text-muted-foreground">Tél: {societe.telephone} - {societe.email}</p>
                </div>

                <Button type="submit" disabled={loading} data-testid="save-societe-btn">
                  {loading ? "Enregistrement..." : "Enregistrer les informations société"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profil
          </CardTitle>
          <CardDescription>Modifiez vos informations personnelles</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input
                  value={profileData.prenom}
                  onChange={(e) => setProfileData({ ...profileData, prenom: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  value={profileData.nom}
                  onChange={(e) => setProfileData({ ...profileData, nom: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
              />
            </div>
            <Button type="submit" disabled={loading}>
              {loading ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Commission Settings (Admin only) */}
      {user?.role === "admin" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" />
              Taux de commission par défaut
            </CardTitle>
            <CardDescription>
              Définissez les taux de commission appliqués par défaut à tous les vendeurs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSettingsUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="taux_ventes" className="flex items-center gap-2">
                    <Percent className="h-4 w-4 text-muted-foreground" />
                    Ventes boutique
                  </Label>
                  <div className="relative">
                    <Input
                      id="taux_ventes"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={globalSettings.taux_commission_ventes}
                      onChange={(e) => setGlobalSettings({ 
                        ...globalSettings, 
                        taux_commission_ventes: parseFloat(e.target.value) || 0 
                      })}
                      className="pr-8"
                      data-testid="settings-taux-ventes"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="taux_sav" className="flex items-center gap-2">
                    <Percent className="h-4 w-4 text-muted-foreground" />
                    SAV / Réparations
                  </Label>
                  <div className="relative">
                    <Input
                      id="taux_sav"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={globalSettings.taux_commission_sav}
                      onChange={(e) => setGlobalSettings({ 
                        ...globalSettings, 
                        taux_commission_sav: parseFloat(e.target.value) || 0 
                      })}
                      className="pr-8"
                      data-testid="settings-taux-sav"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                  </div>
                </div>
              </div>
              
              <div className="p-4 rounded-lg bg-secondary/50 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Settings2 className="h-4 w-4" />
                  Exemple de calcul
                </p>
                <p className="text-sm text-muted-foreground">
                  Vente de <strong>100€</strong> → Commission de <strong>{globalSettings.taux_commission_ventes}€</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  Réparation de <strong>100€</strong> → Commission de <strong>{globalSettings.taux_commission_sav}€</strong>
                </p>
              </div>

              <Button type="submit" disabled={loading} data-testid="save-commission-settings">
                {loading ? "Enregistrement..." : "Enregistrer les taux"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Password Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Sécurité
          </CardTitle>
          <CardDescription>Changez votre mot de passe</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4">
            <div className="space-y-2">
              <Label>Mot de passe actuel</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Confirmer le nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              />
            </div>
            <Button type="submit" disabled>
              Changer le mot de passe
            </Button>
            <p className="text-xs text-muted-foreground">
              Fonctionnalité à venir
            </p>
          </form>
        </CardContent>
      </Card>

      {/* Admin Settings */}
      {user?.role === "admin" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Administration
            </CardTitle>
            <CardDescription>Options avancées pour les administrateurs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg border border-destructive/50 bg-destructive/5">
              <div className="flex items-start gap-3">
                <Trash2 className="h-5 w-5 text-destructive mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-destructive">Effacer les données de démonstration</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    Cette action supprimera tous les clients, appareils, dossiers SAV, ventes et produits de démonstration. 
                    Les comptes utilisateurs ne seront pas affectés.
                  </p>
                  <Button
                    variant="destructive"
                    onClick={handleClearDemoData}
                    disabled={loading}
                    data-testid="clear-demo-btn"
                  >
                    Effacer les données
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
